/**
 * Tarifs dégressifs d'une ligne : « à partir de telle quantité, tel prix
 * unitaire ».
 *
 * <p>Les paliers se saisissent sur la ligne (`l.paliers`, chacun `{q, p}`) et
 * s'enregistrent avec elle. Le prix unitaire `l.p` reste le prix de base ; le
 * prix appliqué est celui du palier le plus élevé que la quantité atteint. Le
 * serveur suit la même règle ({@code Paliers.java}) : sans cela le total
 * affiché différerait de celui du PDF, c'est-à-dire de ce que le client paie.
 */

/**
 * Paliers complets d'une ligne, du plus petit seuil au plus grand.
 *
 * @param {Array<{q: number|string, p: number|string}>} [paliers] paliers saisis
 * @returns {Array<{q: number, p: number}>} les paliers valides, triés
 */
export function paliersValides(paliers) {
  return (paliers || [])
    .map((x) => ({
      q: +String(x?.q ?? '').replace(',', '.'),
      p: +String(x?.p ?? '').replace(',', '.'),
    }))
    .filter((x) => Number.isFinite(x.q) && x.q > 0 && Number.isFinite(x.p) && x.p >= 0)
    .sort((a, b) => a.q - b.q);
}

/**
 * Palier qui s'applique à la quantité de la ligne, s'il y en a un.
 *
 * @param {{q?: number|string, paliers?: Array}} ligne ligne du document
 * @returns {{q: number, p: number}|null} le palier atteint, ou null
 */
export function palierApplique(ligne) {
  const q = +ligne?.q || 0;
  const atteints = paliersValides(ligne?.paliers).filter((x) => q >= x.q);
  return atteints.length ? atteints[atteints.length - 1] : null;
}

/**
 * Prix unitaire effectivement facturé : celui du palier atteint, sinon `l.p`.
 *
 * @param {{p?: number|string, q?: number|string, paliers?: Array}} ligne ligne du document
 * @returns {number} le prix unitaire appliqué
 */
export function prixApplique(ligne) {
  const palier = palierApplique(ligne);
  return palier ? palier.p : +ligne?.p || 0;
}

/**
 * Indique si un tarif dégressif change le prix de la ligne.
 *
 * @param {object} ligne ligne du document
 * @returns {boolean} vrai si le prix facturé n'est pas le prix de base
 */
export function tarifDegressifApplique(ligne) {
  const palier = palierApplique(ligne);
  return palier != null && palier.p !== (+ligne?.p || 0);
}
